import { jsx as _jsx } from "react/jsx-runtime";
import classNames from 'classnames';
import useCallbackRef from '@restart/hooks/useCallbackRef';
import useEventCallback from '@restart/hooks/useEventCallback';
import useWillUnmount from '@restart/hooks/useWillUnmount';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Modal as BaseModal } from '@restart/ui';
import { Fade } from '../Transitions/Fade';
import BootstrapModalManager from './BootstrapModalManager';
import { ModalBody } from './ModalBody';
import ModalContext from './ModalContext';
import { ModalDialog } from './ModalDialog';
import { ModalFooter } from './ModalFooter';
import { ModalHeader } from './ModalHeader';
import { ModalTitle } from './ModalTitle';
import { getScrollbarSize, transitionEnd } from '../dom-helpers';
const manager = new BootstrapModalManager();
/**
 * Use the modal component to add dialogs to your site for lightboxes, user notifications, or completely custom content.
 */ export const Modal = ({ className, style, dialogClassName, contentClassName, children, dialogComponentClass: Dialog = ModalDialog, 'aria-labelledby': ariaLabelledby, /* BaseModal props */ show = false, animation = true, backdrop = true, keyboard = true, onEscapeKeyDown, onShow, onHide, container, autoFocus = true, enforceFocus = true, restoreFocus = true, restoreFocusOptions, onEntered, onExit, onExiting, onEnter, onEntering, onExited, backdropClassName, manager: propsManager, ...props })=>{
    const [modalStyle, setStyle] = useState({});
    const [animateStaticModal, setAnimateStaticModal] = useState(false);
    const waitingForMouseUpRef = useRef(false);
    const ignoreBackdropClickRef = useRef(false);
    const removeStaticModalAnimationRef = useRef(null);
    const [modal, setModalRef] = useCallbackRef();
    const handleHide = useEventCallback(onHide);
    const modalContext = useMemo(()=>({
            onHide: handleHide
        }), [
        handleHide
    ]);
    function getModalManager() {
        if (propsManager) return propsManager;
        return manager;
    }
    function updateDialogStyle(node) {
        const containerIsOverflowing = getModalManager().getScrollbarWidth() > 0;
        const modalIsOverflowing = node.scrollHeight > node.ownerDocument.documentElement.clientHeight;
        setStyle({
            paddingRight: containerIsOverflowing && !modalIsOverflowing ? getScrollbarSize() : undefined,
            paddingLeft: !containerIsOverflowing && modalIsOverflowing ? getScrollbarSize() : undefined
        });
    }
    const handleWindowResize = useEventCallback(()=>{
        if (modal) {
            updateDialogStyle(modal.dialog);
        }
    });
    useWillUnmount(()=>{
        window.removeEventListener('resize', handleWindowResize);
        if (removeStaticModalAnimationRef.current) {
            removeStaticModalAnimationRef.current();
        }
    });
    // We prevent the modal from closing during a drag by detecting where the
    // the click originates from. If it starts in the modal and then ends outside
    // don't close.
    const handleDialogMouseDown = ()=>{
        waitingForMouseUpRef.current = true;
    };
    const handleMouseUp = (e)=>{
        if (waitingForMouseUpRef.current && modal && e.target === modal.dialog) {
            ignoreBackdropClickRef.current = true;
        }
        waitingForMouseUpRef.current = false;
    };
    const handleStaticModalAnimation = ()=>{
        setAnimateStaticModal(true);
        // TODO: hur löser man detta lintfel?
        // eslint-disable-next-line react-compiler/react-compiler
        removeStaticModalAnimationRef.current = transitionEnd(modal?.dialog, ()=>{
            setAnimateStaticModal(false);
        });
    };
    const handleStaticBackdropClick = (e)=>{
        if (e.target !== e.currentTarget) {
            return;
        }
        handleStaticModalAnimation();
    };
    const handleClick = (e)=>{
        if (backdrop === 'static') {
            handleStaticBackdropClick(e);
            return;
        }
        if (ignoreBackdropClickRef.current || e.target !== e.currentTarget) {
            ignoreBackdropClickRef.current = false;
            return;
        }
        onHide?.();
    };
    const handleEscapeKeyDown = (e)=>{
        if (!keyboard) {
            e.preventDefault();
        }
        if (keyboard && backdrop === 'static') {
            // Call preventDefault to stop modal from closing in react-overlays,
            // then play our animation.
            e.preventDefault();
            handleStaticModalAnimation();
        }
        if (keyboard && onEscapeKeyDown) {
            onEscapeKeyDown(e);
        }
    };
    const handleEnter = (node, isAppearing)=>{
        if (node) {
            node.style.display = 'block';
            updateDialogStyle(node);
        }
        if (onEnter) onEnter(node, isAppearing);
    };
    const handleExit = (node)=>{
        if (removeStaticModalAnimationRef.current) {
            removeStaticModalAnimationRef.current();
        }
        if (onExit) onExit(node);
    };
    const handleEntering = (node, isAppearing)=>{
        if (onEntering) onEntering(node, isAppearing);
        // FIXME: This should work even when animation is disabled.
        window.addEventListener('resize', handleWindowResize);
    };
    const handleExited = (node)=>{
        if (node) node.style.display = ''; // RHL removes it sometimes
        if (onExited) onExited(node);
        // FIXME: This should work even when animation is disabled.
        window.removeEventListener('resize', handleWindowResize);
    };
    const renderBackdrop = useCallback((backdropProps)=>/*#__PURE__*/ _jsx("div", {
            ...backdropProps,
            className: classNames('modal-backdrop', backdropClassName, !animation && 'show')
        }), [
        animation,
        backdropClassName
    ]);
    const baseModalStyle = {
        ...style,
        ...modalStyle
    };
    // Sets `display` always block when `animation` is false
    if (!animation) {
        baseModalStyle.display = 'block';
    }
    const renderDialog = (dialogProps)=>/*#__PURE__*/ _jsx("div", {
            role: "dialog",
            ...dialogProps,
            style: baseModalStyle,
            className: classNames(className, 'modal', animateStaticModal && 'modal-static'),
            onClick: backdrop ? handleClick : undefined,
            onMouseUp: handleMouseUp,
            "aria-labelledby": ariaLabelledby,
            children: /*#__PURE__*/ _jsx(Dialog, {
                ...props,
                onMouseDown: handleDialogMouseDown,
                className: dialogClassName,
                contentClassName: contentClassName,
                children: children
            })
        });
    return /*#__PURE__*/ _jsx(ModalContext.Provider, {
        value: modalContext,
        children: /*#__PURE__*/ _jsx(BaseModal, {
            show: show,
            ref: setModalRef,
            backdrop: backdrop,
            container: container,
            keyboard: true,
            autoFocus: autoFocus,
            enforceFocus: enforceFocus,
            restoreFocus: restoreFocus,
            restoreFocusOptions: restoreFocusOptions,
            onEscapeKeyDown: handleEscapeKeyDown,
            onShow: onShow,
            onHide: onHide,
            onEnter: handleEnter,
            onEntering: handleEntering,
            onEntered: onEntered,
            onExit: handleExit,
            onExiting: onExiting,
            onExited: handleExited,
            manager: getModalManager(),
            transition: animation ? Fade : undefined,
            backdropTransition: animation ? Fade : undefined,
            renderBackdrop: renderBackdrop,
            renderDialog: renderDialog
        })
    });
};
Modal.Body = ModalBody;
Modal.Header = ModalHeader;
Modal.Title = ModalTitle;
Modal.Footer = ModalFooter;
Modal.Dialog = ModalDialog;
